import * as Sentry from "@sentry/react";
import { useContext, useEffect, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NotFound } from "../components/NotFound";
import { DisabledScreen } from "../components/DisabledScreen";
import { HeaderProvider } from "../contexts/HeaderContext";
import { api } from "../api/api";
import { ApiError } from "../api/api-backend";
import { resolvePaymentLinkRedirect } from "../pages/pay/payment-link-errors";
import { GetWBEConfigResponse } from "../api/contracts/site-config";
import { usePrepaymentStore, PaymentLinkData } from "../stores/prepaymentStore";
import { AdFormatProvider } from "./AdFormatProvider";
import { getPrivatePage } from "../private-pages";
import { ConfigurationContext } from "./configurationContext";
import { siteTheme, useAppliedTheme } from "../lib/theme/applyTheme";
import { readPreviewTheme } from "../lib/theme/previewTheme";

const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

interface ConfigurationProviderProps {
  children: ReactNode;
}

export function ConfigurationProvider({
  children,
}: ConfigurationProviderProps) {
  const identifier = window.location.pathname.split("/")[1];
  const isExpired = window.location.pathname.includes("/p/expired");
  const isPrivatePage = Boolean(getPrivatePage(window.location.pathname));

  const { t } = useTranslation();
  const { setPaymentLink, setPrePaymentSlug, setPaymentId } =
    usePrepaymentStore();
  const [configuration, setConfiguration] =
    useState<GetWBEConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Slug used for the resolved config, so we can refetch it in the background
  // to keep the logo's short-lived signed URL fresh.
  const [configSlug, setConfigSlug] = useState<string | null>(null);
  const [previewTheme] = useState(() =>
    readPreviewTheme(window.location.search, identifier, sessionStore()),
  );
  useAppliedTheme(
    [document.documentElement],
    siteTheme(configuration, previewTheme),
  );

  useEffect(() => {
    async function loadConfiguration() {
      try {
        setIsLoading(true);
        setError(null);

        // These routes do not belong to a site's WBE configuration.
        if (!identifier || identifier === "404" || isPrivatePage || isExpired) {
          setIsLoading(false);
          return;
        }

        if (identifier === "p") {
          const paymentId = window.location.pathname.split("/")[2];
          const subPath = window.location.pathname.split("/")[3];
          // Paths that must work after payment completion AND must not trigger
          // the "Already paid" → /confirmation redirect. They resolve the site
          // via the metadata endpoint, which doesn't validate payment status.
          const POST_PAYMENT_SUBPATHS = new Set([
            "confirmation",
            "terms",
            "policy",
          ]);

          if (paymentId && POST_PAYMENT_SUBPATHS.has(subPath)) {
            const fetchedPaymentLink: PaymentLinkData = await api.get(
              `/api/public/link/metadata/${paymentId}`,
            );

            // Slug is the source of truth for which payment we're showing.
            // Sync paymentId to the slug-derived paymentRefId so a stale
            // value persisted in sessionStorage from a previous payment does
            // not leak into the new slug's confirmation page.
            setPaymentLink(fetchedPaymentLink);
            setPrePaymentSlug(paymentId);
            setPaymentId(fetchedPaymentLink.paymentRefId ?? null);

            const configData = await api.get<GetWBEConfigResponse>(
              `/api/public/sites/config/${fetchedPaymentLink.siteIdentifier}`,
            );

            setConfiguration(configData);
            setConfigSlug(fetchedPaymentLink.siteIdentifier);
            setIsLoading(false);
            return;
          }

          if (!paymentId) {
            throw new Error("MISSING_PAYMENT_ID");
          }
          // For payment links, we don't want to expose siteId initially,
          // so we're opting to side-load this route until we've
          // verified the payment-link, then we get the sideId as response.

          try {
            const paymentLink: PaymentLinkData = await api.get(
              `/api/public/link/${paymentId}`,
            );

            // Slug is the source of truth. Sync paymentId to the slug-derived
            // paymentRefId (null for unpaid slugs) so a value persisted from
            // a previous payment cannot leak into the surfboard SDK as a
            // stale reservationKey.
            setPaymentLink(paymentLink);
            setPrePaymentSlug(paymentId);
            setPaymentId(paymentLink.paymentRefId ?? null);

            const configData = await api.get<GetWBEConfigResponse>(
              `/api/public/sites/config/${paymentLink.siteIdentifier}`,
            );

            setConfiguration(configData);
            setConfigSlug(paymentLink.siteIdentifier);
          } catch (err) {
            // A paid or expired link has its own destination; anything else is
            // a slug we cannot resolve at all.
            const target =
              err instanceof ApiError
                ? resolvePaymentLinkRedirect(err, paymentId)
                : null;
            window.location.href = target ?? "/404";
            return;
          }
        } else {
          const configData = await api.get<GetWBEConfigResponse>(
            `/api/public/sites/config/${identifier}`,
          );

          if (identifier === "invalid-site") {
            throw new Error("SITE_NOT_FOUND");
          }

          setConfiguration(configData);
          setConfigSlug(identifier);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t("error.failed_to_load_configuration");
        setError(errorMessage);

        if (errorMessage === "SITE_NOT_FOUND") {
          window.location.href = "/404";
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadConfiguration();
  }, [
    t,
    identifier,
    isExpired,
    isPrivatePage,
    setPaymentLink,
    setPrePaymentSlug,
    setPaymentId,
  ]);

  useEffect(() => {
    Sentry.setTag("siteId", configuration?.siteId ?? null);
  }, [configuration?.siteId]);

  // The config carries the header logo as a short-lived signed URL (~15 min
  // TTL). Refresh it well under that TTL so the URL is renewed before it
  // expires; otherwise the logo 404s and the header falls back to the Lyra
  // mark for the rest of the session. Matches the refetch pattern used for the
  // other signed-URL images in WBE (landing/map).
  useEffect(() => {
    if (!configSlug) return;
    const REFRESH_MS = 5 * 60 * 1000;
    const intervalId = setInterval(async () => {
      try {
        const fresh = await api.get<GetWBEConfigResponse>(
          `/api/public/sites/config/${configSlug}`,
        );
        setConfiguration(fresh);
      } catch (err) {
        // Background refresh: keep the last good config on a transient failure
        // and let the next tick retry. Capture for visibility only.
        Sentry.captureException(err);
      }
    }, REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [configSlug]);

  const value = {
    configuration,
    identifier,
    isLoading,
    error,
  };

  if (error) {
    return <NotFound />;
  }

  // CXA-703 kill-switch: if the site has flipped the WBE off, mount the
  // disabled screen instead of the route tree. Sits above HeaderProvider's
  // children so the flag catches every deeplink (start, category, checkout,
  // pay) without each page having to check itself. HeaderProvider still wraps
  // it so the disabled screen can share the language + config context that
  // PayFooter and LanguageDropdown read.
  // Gated on the wbe-disabled-mode feature flag: without it a stored
  // `disabled: true` is ignored and the booking flow renders as before.
  const isDisabled =
    Boolean(configuration?.disabledMode?.disabled) &&
    (configuration?.featureFlags?.includes("wbe-disabled-mode") ?? false);

  return (
    <ConfigurationContext.Provider value={value}>
      <AdFormatProvider>
        <HeaderProvider>{isDisabled ? <DisabledScreen /> : children}</HeaderProvider>
      </AdFormatProvider>
    </ConfigurationContext.Provider>
  );
}

// oxlint-disable-next-line react/only-export-components -- 47 tests mock this module path for the hook
export function useConfiguration() {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error(
      "useConfiguration must be used within a ConfigurationProvider",
    );
  }
  return context;
}

/**
 * The configuration where there is one, undefined where there is not.
 *
 * Some screens render outside the provider entirely — the not-found page, an
 * expired payment link, the communication preferences page — and a shared
 * component used on both sides cannot demand a site that does not exist.
 */
// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the hook
export function useOptionalConfiguration() {
  return useContext(ConfigurationContext);
}
